import { Component, effect, inject, signal } from '@angular/core';

import { CmsDataService, CmsProductItem } from '../../services/cms-data.service';
import { LanguageService } from '../../services/language.service';

interface Testimonio {
  quote: string;
  name: string;
  role: string;
  initials: string;
  color: string;
}

/** Nº de testimonios visibles en la home (diseño: 2 + tarjeta del fundador). */
const HOME_TESTIMONIALS = 2;

/**
 * Sección de confianza / testimonios de la home (diseño "Híbrido v2", rev. jul-2026).
 *
 * Los testimonios se gestionan desde el CMS del gestor (type_key='testimonios'):
 *   título = nombre · subtítulo = cargo/lugar · descripción = la cita.
 * La home muestra los 2 PRIMEROS por orden de aparición (campo `order`,
 * reordenable en la lista del gestor). Mientras el CMS no tenga items se usa
 * el fallback estático (testimonios REALES autorizados por Raúl el 2026-07-11).
 * No publicar testimonios sin autorización de su autor; no inventar métricas.
 */
@Component({
  selector: 'app-clientes-proyectos',
  standalone: true,
  imports: [],
  templateUrl: './clientes-proyectos.component.html',
  styleUrl: './clientes-proyectos.component.scss',
})
export class ClientesProyectosComponent {
  private cms = inject(CmsDataService);
  private langService = inject(LanguageService);

  private readonly palette = [
    'var(--ts-mod-plataforma)',
    'var(--ts-mod-facturacion)',
    'var(--ts-mod-crm)',
    'var(--ts-mod-proyectos)',
  ];

  /** Fallback si el CMS aún no tiene testimonios (reales, autorizados 2026-07-11). */
  private readonly fallback: Testimonio[] = [
    {
      quote:
        'Veníamos de llevarlo todo en Excel y con el miedo de la inspección. Nos montaron el fichaje y la facturación en una tarde.',
      name: 'Miguel Payeras',
      role: 'Gerente · Alcúdia',
      initials: 'MP',
      color: this.palette[0],
    },
    {
      quote:
        'Tengo 22 personas en obra y solo 2 en oficina. Que no me cobren licencia por cada uno que ficha cambió los números.',
      name: 'Toni Quetglas',
      role: 'Construcción y reformas · Marratxí',
      initials: 'TQ',
      color: this.palette[1],
    },
  ];

  testimonios = signal<Testimonio[]>(this.fallback);

  // Autoría/solidez y estándar de servicio son afirmaciones separadas; no
  // prometer "siempre el fundador al teléfono".
  founder = {
    kicker: 'Quién hay detrás',
    body: 'Detrás hay 15 años haciendo software para empresas. Y cuando llames, te atiende alguien del equipo que lo ha construido — no un call center.',
    name: 'Raúl Alemany Pérez',
    role: 'Fundador · TodoSector',
    photo: 'assets/img/founder/raul-alemany.jpg',
  };

  badges = [
    { label: 'Te atiende quien conoce tu cuenta', color: 'var(--ts-primary)' },
    { label: 'Soporte cercano, en tu idioma', color: 'var(--ts-mod-facturacion)' },
    { label: 'Puesta en marcha opcional', color: 'var(--ts-mod-crm)' },
  ];

  constructor() {
    effect(() => {
      const lang = this.langService.currentLang();
      void this.load(lang);
    });
  }

  private async load(lang: string): Promise<void> {
    const items = await this.cms.getCmsItems('testimonios', lang);
    if (items.length === 0) return; // CMS vacío → se mantiene el fallback

    this.testimonios.set(
      items
        .slice(0, HOME_TESTIMONIALS)
        .map((item, i) => this.toTestimonio(item, i))
    );
  }

  private toTestimonio(item: CmsProductItem, index: number): Testimonio {
    return {
      quote: this.stripHtml(item.description) || item.subtitle,
      name: item.title,
      role: item.subtitle,
      initials: this.initialsOf(item.title),
      color: this.palette[index % this.palette.length],
    };
  }

  /** La cita llega como HTML del editor del CMS; en la tarjeta va como texto plano. */
  private stripHtml(html: string): string {
    return (html || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private initialsOf(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w.charAt(0).toUpperCase())
      .join('');
  }
}
